import type { NextApiRequest, NextApiResponse } from 'next'
import type { Language, Option, PaymentProviderConfig, ShippingProviderConfig } from 'ecosystem'
import storefrontApi from 'api/storefront-api'
import { type OrderProvisionResult } from 'api/storefront-api/ordersEndpoint'
import { useLocaleCredentials as setLocaleCredentials } from 'api'
import { handlerWithLogging } from '../../logging'

interface PushOrderHandlerOpts {
  paymentProviderConfig?: PaymentProviderConfig
  shippingProviderConfig?: ShippingProviderConfig
}

export const pushOrderHandler = handlerWithLogging(
  async (
    req: NextApiRequest,
    res: NextApiResponse<{ message: string } | { error: string }>,
    opts: PushOrderHandlerOpts
  ) => {
    const locale = (req.cookies.NEXT_LOCALE as Language) || 'sv'
    setLocaleCredentials(locale)
    if (req.method !== 'POST') {
      res.status(405).json({ error: 'Method not supported.' })
      return
    }

    const queryOrderId = req.query.order_id

    if (!queryOrderId) {
      res.status(400).json({ error: 'Missing query params.' })
      return
    }

    const orderId = String(queryOrderId)

    // Small head-start so the confirmation page usually provisions first; correctness no longer
    // relies on this delay — provisionOrder is idempotent (skips if the order is already
    // provisioned). Keep it short: Nexi requires a 200 within 10s total, so delay + provisioning
    // must stay well under that budget or Nexi will retry and trigger another provision attempt.
    await new Promise((resolve) => {
      setTimeout(resolve, 2000)
    })

    let provisionResult: Option<OrderProvisionResult> = null
    let provisionError

    try {
      provisionResult = await storefrontApi.orders.provisionOrder(
        orderId || '',
        opts.paymentProviderConfig,
        opts.shippingProviderConfig,
        // The push is a fallback: it provisions only orders the confirmation page never handled
        // (still CREATED and older than the freshness window). Fresh orders throw here → 400 → the
        // provider retries later. This keeps the confirmation page the single provisioner during
        // the post-checkout window, so the two paths never provision the same order concurrently.
        { asFallback: true }
      )
    } catch (e) {
      if (e instanceof Error) {
        provisionError = e.toString()
      } else {
        provisionError = 'unknown'
      }
    }

    if (provisionError || !provisionResult) {
      res
        .status(400)
        .json({ error: provisionError || 'unknown error while provisioning the order' })
      return
    }

    res.status(200).json({ message: 'Success' })
  }
)
